import { shallowRef } from 'vue';
import { apiService as defaultApiService } from '@/services/api/index.js';
import { toastManager } from '@/composables/useToasts.js';
import { useSearchResultsStore } from '@/store/searchResults';
import { createTripSearchRequest } from '@/services/tripSearch/tripSearchMapper.js';

export function useTripSearch({
  router,
  closeTripFinderModal,
  searchResultsStore = useSearchResultsStore(),
  apiService = defaultApiService,
  success = toastManager.success,
  error = toastManager.error,
} = {}) {
  const isTripFinderSearching = shallowRef(false);
  const showLoadingOverlay = shallowRef(false);
  const loadingMessage = shallowRef('Searching...');
  const loadingSubMessage = shallowRef(null);

  const resetStoredSearch = () => {
    sessionStorage.removeItem('searchResults');
    sessionStorage.removeItem('searchParams');
    sessionStorage.removeItem('tripFinderMode');
  };

  const streamDestinations = async (apiParams) => {
    const destinations = [];

    await new Promise((resolve, reject) => {
      apiService.buildTripStream(apiParams, {
        onStatus: () => {
          loadingSubMessage.value = 'Finding the best options for you...';
        },
        onBatch: (data) => {
          const results = data.options || data.suggestions || [];
          destinations.push(...results);
          loadingSubMessage.value = `Found ${destinations.length} destination${destinations.length !== 1 ? 's' : ''} so far...`;
        },
        onComplete: () => {
          showLoadingOverlay.value = false;
          resolve();
        },
        onError: (streamError) => {
          showLoadingOverlay.value = false;
          reject(new Error(streamError.error || 'Streaming failed'));
        },
      });
    });

    return destinations;
  };

  const handleTripSearch = async (searchData) => {
    isTripFinderSearching.value = true;

    try {
      const apiParams = createTripSearchRequest(searchData);

      resetStoredSearch();
      closeTripFinderModal?.();
      loadingMessage.value = 'Searching for destinations...';
      loadingSubMessage.value = 'Preparing your results';
      showLoadingOverlay.value = true;

      const destinations = await streamDestinations(apiParams);

      searchResultsStore.saveSearchResults(destinations, searchData, apiParams.mode);
      // SearchResults.vue watches the store ref directly — no DOM event needed.

      const resultCount = destinations.length;
      success(`✈️ Found ${resultCount} amazing destination${resultCount !== 1 ? 's' : ''}!`);
      await router.push({ name: 'SearchResults' });
    } catch {
      error('Failed to search for trips. Please try again.');
    } finally {
      isTripFinderSearching.value = false;
      showLoadingOverlay.value = false;
    }
  };

  return {
    handleTripSearch,
    isTripFinderSearching,
    showLoadingOverlay,
    loadingMessage,
    loadingSubMessage,
  };
}
